import { Modal } from 'antd';
import { useSession } from 'next-auth/react';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, UseFormHandleSubmit, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EditUserFormValues } from '../../../types/userform';
import useGroups from '../../hooks/adminPage/useGroups';
import useFormName from '../../hooks/ticketDetailPage/useFormName';
import useUserById, { deriveCustomizedDetailedUser } from '../../hooks/useUserById';
import { usePaginatedUserFieldDefinitionListNoQuaryParams } from '../../hooks/useUserFieldDefinitionList';
import { FieldDefinitionMap, GroupWithUsers, RequesterEditPostData } from '../../model/types';
import logout from '../../services/auth/logout';
import { updateUser } from '../../services/people/userServices';
import { useCurrentUserStore } from '../../store/RootStoreContext';
import { grispiNotification } from '../../store/useNotificationStore';
import { isAdminOrAgent } from '../../utils/accountHelper';
import { controlledUserFormValue } from '../../utils/forms/formHelper.parser';
import { nonNullishValue, nullishValue } from '../../utils/helpers';
import requestWithMessage from '../../utils/requestWithMessage';
import { getSessionStorageItem } from '../../utils/storageHelpers';
import { hasFieldUserValueType } from './CreateTicketForm';
import { removeUnchangedTicketFieldValues } from './EditTicketForm';
import FormPersistController from './FormPersistController';

interface Props {
    defaultValues: EditUserFormValues;
}

const getEditUserPostDataFromFormValues = (
    defaultValues: EditUserFormValues,
    formValues: EditUserFormValues,
    fieldListMap: FieldDefinitionMap,
    groupList: GroupWithUsers[]
): RequesterEditPostData => {
    const emails = formValues.secondaryEmails;
    emails.push(formValues.primaryEmail);

    const phones = formValues.secondaryPhones;
    formValues.primaryPhone && phones.push(formValues.primaryPhone);

    const postDataFields: RequesterEditPostData['fields'] = [];
    const groups = groupList
        .map(({ id, name }) => ({ id, name }))
        .filter((group) => formValues?.groups?.split(',').includes(group.id.toString()));
    Object.entries(removeUnchangedTicketFieldValues(formValues, defaultValues)['fields']).forEach(([key, value]) => {
        const postKey = key.replaceAll('$', '.');
        const postValue =
            typeof value === 'string'
                ? hasFieldUserValueType(fieldListMap[postKey])
                    ? controlledUserFormValue(value)
                    : value
                : value;

        postDataFields.push({
            key: postKey,
            value: postValue
        });
    });
    return {
        email: formValues.primaryEmail.email ?? '',
        emails: emails?.map((email) => email.email).filter((email) => !nullishValue(email) && email !== ''),
        fields: postDataFields,
        fullName: formValues.fullName,
        groups: groups.length > 0 ? groups : null,
        organization: formValues.organization,
        phone: formValues.primaryPhone.phone ?? '', // back-end will know it should be deleted
        phones: phones?.map((phone) => phone.phone).filter((phone) => !nullishValue(phone) && phone !== ''),
        role: formValues.role,
        ...(formValues.role !== formValues.roleToChange ? { roleToChange: formValues.roleToChange } : {}),
        userId: formValues.userId
    };
};

export default function EditUserForm({ children, defaultValues }: PropsWithChildren<Props>) {
    const { t } = useTranslation('people');

    const { data, mutate: mutateUser } = useUserById(defaultValues.userId?.toString());
    const requesterInfo = deriveCustomizedDetailedUser(data);
    const { data: fieldDefinitionList } = usePaginatedUserFieldDefinitionListNoQuaryParams({ revalidateOnMount: true });
    const { data: groupList } = useGroups();
    const {
        data: {
            userInfo: { username, userId }
        }
    } = useSession();
    const formName = useFormName();
    const [formHydrated, setFormHydrated] = useState(getSessionStorageItem(formName) ? false : true);
    const { refreshStore, id: currentUserId } = useCurrentUserStore();
    const methods = useForm<EditUserFormValues>({
        mode: 'onChange',
        defaultValues,
        shouldFocusError: true
    });
    const [modal, contextHolder] = Modal.useModal();
    const isCurrentUser = useMemo(() => currentUserId === requesterInfo?.id, [currentUserId, requesterInfo?.id]);
    const { handleSubmit, reset, setValue, trigger } = methods;

    useEffect(() => {
        const persistedFormValues = getSessionStorageItem(formName);

        if (persistedFormValues && !formHydrated) {
            Object.entries(persistedFormValues).forEach(([key, value]) => {
                if (nonNullishValue(value) && typeof value === 'object' && !Array.isArray(value)) {
                    Object.entries(value).forEach(([subKey, subValue]) => {
                        if (nonNullishValue(subValue) && typeof subValue === 'object' && !Array.isArray(subValue)) {
                            Object.entries(subValue).forEach(([subSubKey, subSubValue]) => {
                                setValue(`${key}.${subKey}.${subSubKey}` as any, subSubValue, { shouldDirty: true });
                            });
                            return;
                        }

                        setValue(`${key}.${subKey}` as any, subValue, { shouldDirty: true });
                    });
                }
                setValue(key as any, value, { shouldDirty: true });
            });
            setFormHydrated(true);
            trigger();
            return;
        }
    }, [formHydrated, formName, setValue, trigger]);

    const onValid: SubmitHandler<EditUserFormValues> = async (formValues) => {
        const { fields } = formValues;
        // TODO: role-update => we should send only dirtyFields with required params if any, otherwise we do unnecessary updateUser requests when only role is changed
        const postData = getEditUserPostDataFromFormValues(
            defaultValues,
            formValues,
            fieldDefinitionList.listMap,
            groupList
        );
        const startMessage = t('startMessage.onEdit', { fullName: requesterInfo.fullName });
        const finishMessage = t('finishMessage.onEdit');

        const requestName = 'edit-requester';
        const userIsAgentOrAdmin = isAdminOrAgent(postData?.role);
        if (
            defaultValues?.primaryEmail?.email !== formValues?.primaryEmail?.email &&
            userIsAgentOrAdmin &&
            !formValues?.primaryEmail?.verified
        ) {
            const showConfirm = () => {
                modal.warning({
                    title: t('emailChangeForAgentOrAdminMessage.title'),
                    content: (
                        <>
                            <div>{t('emailChangeForAgentOrAdminMessage.content')}</div>
                            <div>{t('emailChangeForAgentOrAdminMessage.content1')}</div>
                        </>
                    )
                });
            };

            showConfirm();
            return;
        }
        // check if email address of currentUser is going to be changed and if it is, logout user from all sessions (email change will cause accessToken to be invalidated)
        if (isCurrentUser && postData?.email !== username) {
            const showConfirm = () => {
                modal.confirm({
                    title: t('emailChangeMessage.title'),
                    content: (
                        <>
                            <div>{t('emailChangeMessage.content1')}</div>
                            <div>{t('emailChangeMessage.content')}</div>
                        </>
                    ),
                    closable: true,
                    onOk: async () => {
                        try {
                            await updateUser(postData);
                            // redirect uses NEXTAUTH_URL which doesn't support dynamic subdomains
                            // i.e. cayci.grispi.com logout redirects to grispi.com which is wrong
                            await logout();
                        } catch (error) {
                            grispiNotification('error', error?.response?.data?.message ?? error, 'error');
                        }
                    }
                });
            };

            showConfirm();
            return;
        }
        await requestWithMessage({
            key: requestName,
            startMessage,
            finishMessage,
            apiRequest: async () => {
                await updateUser(postData);
                await mutateUser();
                // TODO: performance optimization, !find an another way to update CurrentUserStore without requesting endpoint again via refreshStore method
                if (userId === requesterInfo?.id) await refreshStore(userId); // on store

                const newFormValues = {
                    ...formValues,
                    role: formValues.roleToChange,
                    secondaryEmails: [
                        ...new Set(formValues.secondaryEmails.filter((email) => email !== formValues.primaryEmail))
                    ],
                    secondaryPhones: [
                        ...new Set(formValues.secondaryPhones.filter((phone) => phone !== formValues.primaryPhone))
                    ]
                };
                reset({ ...newFormValues, fields });
            }
        });
    };

    const onSubmit = useCallback(
        (handleOnSubmit: ReturnType<UseFormHandleSubmit<EditUserFormValues>>) => (e?: React.BaseSyntheticEvent) =>
            handleOnSubmit(e).catch(() => {
                return;
            }),
        []
    );

    return (
        <FormProvider {...methods}>
            <FormPersistController formName={formName} />
            <form onSubmit={onSubmit(handleSubmit(onValid))} id="edit-user-form">
                {children}
            </form>
            {contextHolder}
        </FormProvider>
    );
}
